import { PageTitle } from '_metronic/layout/core'

import StepChip from 'Presentation/Components/StepChip'
import KumopackStepper from 'Presentation/Components/KumopackStepper'
import { ClientSideTable } from 'Presentation/Components/Table'

import useViewModel from './ViewModel'
import moment from 'moment'
import FormGenerator from 'Presentation/Components/Form/FormGenerator'
import formatNumberCommas from 'Utils/formatNumberCommas'
import clsx from 'clsx'

//////////
// View //
//////////

const QuotationDetailView: React.FC = () => {
	const {
		disabledInput,
		isNegotitated,
		negotitateRemark,
		canSend,
		isLoading,
		isSubmitting,
		data,
		items,
		financial,
		net,
		netBeforeVat,
		vatAmount,
		TABLE_COLUMNS,
		onBack,
		onUpdateItem,
		onSendItem,
		onAddNewItem,
		onChangeDiscount,
		onChangeVatCalculated,
	} = useViewModel()

	return (
		<ViewPageTitle>
			<div className="row gy-5">
				<div className="col-12">
					<div className="card border-kumopack-grey-200">
						{/* Header */}
						<div className="card-header">
							<div className="card-title">
								<h4 className="text-kumopack-grey-900 fw-bold mb-0">
									หมายเลขที่ใบคำร้อง:{' '}
									<span className="fw-normal">
										{String(data?.code).length === 0 || !data?.code ? '-' : data?.code}
									</span>
								</h4>
							</div>

							<div className="card-toolbar">
								<StepChip stepLabel={data?.statusTitleTh ?? ''} />
							</div>
						</div>

						<div className="card-body">
							<div className="row gy-5">
								{/* Stepper */}
								<div className="col-12">
									<KumopackStepper
										color="secondary"
										items={[
											{
												label: 'ส่งคำขอ',
												status: 'done',
												description: data?.inquiry?.createdAt
													? moment(data?.inquiry?.createdAt).format('DD/MM/YYYY HH:mm')
													: '-',
											},
											{
												label: 'ส่งใบเสนอราคา',
												status:
													data?.documentStatus === '40'
														? 'done'
														: data?.documentStatus === '20'
														? 'done'
														: 'active',
												description: data?.sentAt
													? moment(data?.sentAt).format('DD/MM/YYYY HH:mm')
													: '-',
											},
											{
												label: 'ได้รับการตอบรับ',
												status: data?.approvedAt ? 'done' : 'pending',
												description: data?.approvedAt
													? moment(data?.approvedAt).format('DD/MM/YYYY HH:mm')
													: '-',
											},
										]}
									/>
								</div>

								{isNegotitated && (
									<div className="col-12">
										<div className="alert alert-primary bg-kumopack-secondary-50 border-kumopack-secondary-700">
											<div className="alert-text fw-bold fs-5 text-kumopack-secondary-700">
												ผู้ซื้อต้องการเจรจาราคา : {negotitateRemark}
											</div>
										</div>
									</div>
								)}

								{canSend && (
									<div className="col-12">
										<div className="alert alert-primary bg-kumopack-primary-50 border-kumopack-primary-700">
											<div className="alert-text fw-bold fs-5 text-kumopack-primary-700">
												คำเตือน : ราคาของสินค้าที่กรอกในแบบฟอร์ม ต้องเป็นราคาสินค้าต่อหน่วย
												และไม่รวมภาษี
											</div>
										</div>
									</div>
								)}

								{/* OEM Header */}
								<div className="col-12">
									<div className="card border border-kumopack-grey-200">
										<div className="card-header">
											<div className="card-title">
												<h4
													className="d-inline-block text-kumopack-grey-900 fw-bold me-2"
													style={{ marginInline: 0, marginBlock: 0 }}>
													All Items
												</h4>
												<span
													className="d-inline-block badge badge-kumopack-secondary-50 text-kumopack-secondary-700"
													style={{ borderRadius: '16px' }}>
													{items.length} items
												</span>
											</div>
										</div>

										{/* Price Table */}
										<div className="card-body p-0">
											<ClientSideTable
												isLoading={isLoading}
												data={items}
												columns={TABLE_COLUMNS}
												items_per_page={9999}
											/>

											{canSend && (
												<>
													<hr
														style={{
															background: 'transparent',
															borderBottom: '1px solid #CCC',
															borderTop: 0,
															borderLeft: 0,
															borderRight: 0,
															margin: 0,
														}}
													/>

													<div
														onClick={onAddNewItem}
														className="d-flex flex-row justify-content-center align-items-center p-5 transition-300 bg-hover-kumopack-grey-50 hover-enabled cursor-pointer"
														style={{
															columnGap: '12px',
														}}>
														<img
															src="/media/icons/kumopack/circle-add-grey.svg"
															alt="Circle Add Grey Icon"
															style={{ userSelect: 'none', pointerEvents: 'none' }}
														/>
														<div className="text-kumopack-grey-900 fw-bold fs-4">
															เพิ่มรายการใหม่
														</div>
													</div>
												</>
											)}

											<hr
												style={{
													background: 'transparent',
													borderBottom: '1px solid #CCC',
													borderTop: 0,
													borderLeft: 0,
													borderRight: 0,
													margin: 0,
												}}
											/>

											<div className="row py-5 px-5 px-xl-8 gy-5">
												{/* begin:: Table */}
												<div className="d-none d-xl-block col-8" />
												<div className="col-6 col-xl-2 text-end fs-3 fw-bold">ส่วนลด</div>
												<div className="col-6 col-xl-2 text-end fs-3 text-kumopack-grey-400">
													<FormGenerator
														formKey="discount"
														inputType="plain"
														value={financial.discount}
														onChange={e => onChangeDiscount(e.target.value)}
														disabled={isSubmitting || !canSend}
														placeholder="0.00"
														containerClassName=""
														additionalClassName="form-control-sm w-150px ms-auto text-end"
														isHorizontal
													/>
												</div>

												<div className="d-none d-xl-block col-8" />
												<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
													ภาษีมูลค่าเพิ่ม 7%
												</div>
												<div className="col-6 col-xl-2 text-end fs-3 text-kumopack-grey-400 d-flex flex-row align-items-center justify-content-end">
													<input
														name="isRemember"
														className={clsx('d-inline-block form-check-input cursor-pointer', {
															'bg-kumopack-primary-600 border-kumopack-base-white':
																financial.isVatCalculated,
															'border-kumopack-gray-700 bg-kumopack-grey-100':
																!financial.isVatCalculated,
														})}
														style={{
															backgroundSize: financial.isVatCalculated ? 'initial' : 'contain',
														}}
														type="checkbox"
														checked={financial.isVatCalculated}
														onChange={e => {
															if (!disabledInput) onChangeVatCalculated(e.target.checked)
														}}
													/>
													{
														// @ts-ignore
														<span className="ms-2 fs-5">(฿ {formatNumberCommas(vatAmount)})</span>
													}
												</div>

												{/* <div className="d-none d-xl-block col-4" />
												<div className="col-6 col-xl-4 text-end fs-3 fw-bold">
													ภาษีที่หัก ณ​ ที่จ่าย
												</div>
												<div className="col-6 col-xl-4 text-end fs-3 text-kumopack-grey-400">
													<FormGenerator
														formKey="vat"
														inputType="plain"
														placeholder="0.00"
														disabled
														// value={financial.discount}
														// onChange={e => onChangeDiscount(e.target.value)}
														containerClassName=""
														additionalClassName="form-control-sm w-150px ms-auto text-end"
														isHorizontal
													/>
												</div> */}
												{/* 
												<div className="d-none d-xl-block col-4" />
												<div className="col-6 col-xl-4 text-end fs-3 fw-bold">ส่วนลด</div>
												<div className="col-6 col-xl-4 text-end fs-3 text-kumopack-grey-400">
													฿ 0.00
												</div> */}
												<div className="d-none d-xl-block col-8" />
												<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
													ยอดรวมยังไม่รวมภาษี
												</div>
												<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
													฿ {!isNaN(netBeforeVat) ? formatNumberCommas(netBeforeVat) : 0.0}
												</div>

												<div className="d-none d-xl-block col-8" />
												<div className="col-6 col-xl-2 text-end fs-3 fw-bold">ยอดรวมสุทธิ</div>
												<div className="col-6 col-xl-2 text-end fs-3 fw-bold">
													฿ {!isNaN(net) ? formatNumberCommas(net) : 0.0}
												</div>
												{/* end:: Table */}
											</div>

											<hr
												style={{
													background: 'transparent',
													borderBottom: '1px solid #CCC',
													borderTop: 0,
													borderLeft: 0,
													borderRight: 0,
													margin: 0,
												}}
											/>

											<div className="text-kumopack-grey-700 p-5">
												สร้างเมื่อวันที่ {moment(data?.createdAt).format('DD-MM-YYYY')}
											</div>
										</div>
									</div>
								</div>

								{/* Confirm Button */}
								<div className="col-12 text-end">
									<div className="d-flex flex-row justify-content-between">
										<button
											className="btn btn-sm white-button text-kumopack-grey-700 fw-bold"
											onClick={onBack}
											disabled={isSubmitting}>
											ย้อนกลับ
										</button>

										{canSend && (
											<div className="d-flex flex-row" style={{ gap: '8px' }}>
												<button
													className="btn btn-sm white-button fw-bold"
													onClick={onUpdateItem}
													disabled={isSubmitting || isLoading}>
													บันทึกเป็นแบบร่าง
												</button>

												<button
													className="btn btn-sm btn-kumopack-secondary-600 text-kumopack-base-white fw-bold"
													onClick={onSendItem}
													disabled={isSubmitting || isLoading}>
													<img
														className="me-1"
														src="/media/icons/kumopack/message-chat-circle-white.svg"
														alt="White Message Icon"
														style={{ userSelect: 'none', pointerEvents: 'none' }}
													/>
													ส่งใบเสนอราคา
												</button>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ViewPageTitle>
	)
}

/////////////////////
// View Page Title //
/////////////////////

const ViewPageTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	return (
		<>
			<PageTitle
				breadcrumbs={[
					{
						title: 'OEM',
						path: '/oem',
						isActive: false,
						isSeparator: false,
					},
					{
						title: 'OEM Details',
						path: '#',
						isActive: true,
						isSeparator: false,
					},
				]}>
				รายละเอียดใบเสนอราคา
			</PageTitle>

			{children}
		</>
	)
}

export default QuotationDetailView
